import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Share2, RefreshCw, EyeOff, ExternalLink, Copy } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Owner-side UI for the Trusted Guest Badge (Public shareable SBT profile).
 * Props:
 *  - wallet: connected wallet address (required to enable/rotate/disable)
 */
export default function TrustedBadgeShareCard({ wallet }) {
  const [state, setState] = useState({ handle: null, enabled: false });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const api = `${BACKEND_URL}/api/trusted-guest`;

  const load = async () => {
    if (!wallet) return;
    try {
      const r = await axios.get(`${api}/me/${wallet}`);
      setState(r.data);
    } catch {
      /* no-op */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [wallet]); // eslint-disable-line react-hooks/exhaustive-deps

  const call = async (path) => {
    setActing(true);
    try {
      const r = await axios.post(`${api}${path}`, { wallet });
      if (r.data?.handle !== undefined) {
        setState({ handle: r.data.handle, enabled: true });
      } else {
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  if (!wallet) return null;
  if (loading) return null;

  const publicUrl = state.handle ? `${window.location.origin}/trusted/${state.handle}` : '';

  const copy = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success('Public badge link copied');
  };

  if (!state.handle || !state.enabled) {
    return (
      <Card className="border-dashed border-amber-500/40 bg-amber-500/5" data-testid="trusted-badge-share-card">
        <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="font-medium">Shareable Trusted Badge</p>
              <p className="text-xs text-muted-foreground">
                A public, zero-PII link that proves your age + trusted-guest status. Use it at bars, events, venues.
              </p>
            </div>
          </div>
          <Button
            onClick={() => call('/me/enable')}
            disabled={acting}
            className="bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="trusted-badge-enable">
            <Share2 className="w-4 h-4 mr-2" />
            {state.handle ? 'Re-enable' : 'Enable'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-emerald-500/40 bg-emerald-500/5" data-testid="trusted-badge-share-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="font-medium">Trusted Badge is LIVE</p>
              <p className="text-xs text-muted-foreground">Anyone with this link sees your trusted status — no PII.</p>
            </div>
          </div>
          <Badge variant="outline" className="border-emerald-500/40 text-emerald-400">Public</Badge>
        </div>

        <div className="flex items-center gap-2 bg-background rounded-md border px-3 py-2">
          <code className="text-xs truncate flex-1" data-testid="trusted-badge-url">{publicUrl}</code>
          <Button size="sm" variant="ghost" onClick={copy} data-testid="trusted-badge-copy">
            <Copy className="w-3.5 h-3.5" />
          </Button>
          <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="trusted-badge-open">
            <Button size="sm" variant="ghost">
              <ExternalLink className="w-3.5 h-3.5" />
            </Button>
          </a>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            disabled={acting}
            onClick={() => call('/me/rotate')}
            data-testid="trusted-badge-rotate">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Rotate link
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={acting}
            onClick={() => call('/me/disable')}
            data-testid="trusted-badge-disable">
            <EyeOff className="w-3.5 h-3.5 mr-1.5" /> Make private
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
